import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, ViewChildren } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { Bundle } from 'src/app/bundle-edit/models/bundle';
import { BooleanModalComponent } from 'src/app/shared/components/boolean-modal/boolean-modal.component';
import { BooleanModalData } from 'src/app/shared/models/boolean-modal-data';
import { Activity } from '../../models/activity';
import { BundleItem } from '../../models/bundle-item';
import { BundleItemPricingCategory } from '../../models/bundle-item-pricing-category';
import { BundleItemStartTime } from '../../models/bundle-item-start-time';
import { BundleItemStartTimeCondition } from '../../models/bundle-item-start-time-condition';
import { ItemModalData } from '../../models/item-modal-data';
import { PricingCategory } from '../../models/pricing-category';
import { InventorySystem } from 'src/app/shared/models/inventory-system.enum';
import { InvSysDataService } from '../../services/inv-sys-data.service';
import { BundleItemComponent } from '../bundle-item/bundle-item.component';
import { ItemModalComponent } from '../item-modal/item-modal.component';
import { formatMinutes, MINUTES_PER_DAY, parseTimeToMinutes } from '../../utils/start-time.utils';

/** A single violation of the within-day sequential start-time rule. */
export interface StartTimeViolation {
  dayNo: number;
  position: number;
  title: string;
  // The message to show the manager (mirrors the matching server-side error text for blocking rules).
  message: string;
  // True for a structural issue that blocks save; false for a soft overlap warning the manager may override.
  blocking: boolean;
}

@Component({
  selector: 'bundle-form',
  templateUrl: './bundle-form.component.html',
  styleUrls: ['./bundle-form.component.scss']
})
export class BundleFormComponent implements OnInit, OnDestroy {
  @ViewChildren(BundleItemComponent) private bundleItems: QueryList<BundleItemComponent>;
  @Input() isNew: boolean;
  @Input() bundle: Bundle;
  @Input() isSubmitLoading: boolean;
  @Output() formSubmitted = new EventEmitter<null>();

  bundleForm: UntypedFormGroup;
  private baselineFormSnapshot: string = null;
  // Start-time violations recomputed once per form value change (not per change-detection pass) — see ngOnInit.
  startTimeViolations: StartTimeViolation[] = [];
  // Per-day/per-item start-time info, also recomputed once per value change. Keeps array identity stable across
  // change-detection passes (so [firstItemStartTimes] doesn't churn) and avoids re-walking the day per pass.
  private daysStartTimeInfo: {
    firstItemStartTimes: string[];
    firstItemOccupiedMinutes: number;
    itemBarriers: (number | null)[];
    itemIsFirstActive: boolean[];
  }[] = [];

  get formValue(): Bundle {
    return this.bundleForm.getRawValue();
  }

  get bundleCategory(): UntypedFormControl {
    return this.bundleForm.get('category') as UntypedFormControl;
  }

  get pricingCategories(): UntypedFormArray {
    return this.bundleForm.get('pricingCategories') as UntypedFormArray;
  }

  set pricingCategories(categories: UntypedFormArray) {
    this.bundleForm.setControl('pricingCategories', categories);
  }

  get days(): UntypedFormArray {
    return this.bundleForm.get('days') as UntypedFormArray;
  }

  set days(days: UntypedFormArray) {
    this.bundleForm.setControl('days', days);
  }

  get discount(): UntypedFormControl {
    return this.bundleForm.get('discount') as UntypedFormControl;
  }

  get conditionalStartTimes(): UntypedFormControl {
    return this.bundleForm.get('conditionalStartTimes') as UntypedFormControl;
  }

  private formValueChanges: Subscription;
  private conditionalToggleChanges: Subscription;

  constructor(
    private fb: UntypedFormBuilder,
    private invSysDataService: InvSysDataService,
    private cdk: ChangeDetectorRef,
    public dialog: MatDialog,
  ) { }

  /**
   * A Liva product must always be 0% discount, so its own discount is forced to 0 and its input is always
   * disabled. Only the Liva item is affected — the bundle discount and other (Bokun) items stay editable.
   * Runs live off valueChanges so a newly added Liva item is locked immediately. getRawValue() (used on save)
   * keeps the disabled 0.
   */
  private enforceLivaDiscountRule(): void {
    this.days.controls.forEach((day: AbstractControl) => {
      const items = day.get('items') as UntypedFormArray;
      items?.controls.forEach(item => {
        if (item.value?.invSys !== InventorySystem.Liva) {
          return;
        }
        const discount = item.get('discount') as UntypedFormControl;
        if (!discount) {
          return;
        }
        // Force an EXPLICIT 0. A blank value would otherwise fall back to the bundle discount (shown via the
        // input's placeholder and applied server-side as item.Discount ?? bundle.Discount), so a Liva item must
        // carry a literal 0 — not be left empty. Compare against the literal 0 (Number(null) === 0 would skip it).
        if (discount.value !== 0) {
          discount.setValue(0, { emitEvent: false });
        }
        if (discount.enabled) {
          discount.disable({ emitEvent: false });
        }
      });
    });
  }

  ngOnInit(): void {
    this.loadBundleForm(this.bundle);
    // The sequential start-time check applies to ALL items (Bokun and Liva). It is still safe to run on legacy
    // (all-Bokun) bundles because classic-mode overlaps are non-blocking: sequentialStartTimesValidator only errors
    // on `.some(v => v.blocking)`, and blocking violations come exclusively from the conditional branch. So an
    // existing bundle can surface overlap WARNINGS it didn't before, but can never be made unsaveable by this.
    this.days.setValidators(this.sequentialStartTimesValidator);
    this.days.updateValueAndValidity();
    this.enforceLivaDiscountRule();
    this.recomputeStartTimeInfo();
    this.formValueChanges = this.bundleForm.valueChanges.subscribe(() => {
      this.enforceLivaDiscountRule();
      // Self-heal conditional mappings orphaned by removing a first-tour start time, then re-run the day validator so
      // the (now-cleared) blocking violation releases Save. Both mutate with emitEvent:false, so no re-entrancy.
      this.pruneOrphanedTriggerConditions();
      this.days.updateValueAndValidity({ emitEvent: false });
      // Recompute violations + per-day/per-item start-time info ONCE per value change; the template binds the fields
      // instead of calling these walkers on every change-detection pass (and per keystroke via cdk.detectChanges).
      this.recomputeStartTimeInfo();
      this.cdk.detectChanges();
    });
    // Switching the conditional-start-times mode changes which start-time rule applies, so re-validate the days.
    this.conditionalToggleChanges = this.conditionalStartTimes.valueChanges
      .subscribe(() => this.days.updateValueAndValidity());
    // Baseline for unsaved-changes detection, captured once the form is loaded and the Liva-discount rule applied.
    this.resetBaseline();
  }

  /**
   * Snapshot the current form as the "saved" baseline for unsaved-changes detection. Compared form-shape vs
   * form-shape (getRawValue): the server model has a different shape — rateIdInInvSys is a string there but a
   * number here, plus generated ids — so comparing the form against the server model always reports "changed".
   * Call after the form loads and after every successful save.
   */
  resetBaseline(): void {
    this.baselineFormSnapshot = this.serializeForm();
  }

  /** True when the form differs from the last-saved baseline. */
  hasUnsavedChanges(): boolean {
    return this.baselineFormSnapshot !== null && this.serializeForm() !== this.baselineFormSnapshot;
  }

  /**
   * Serializes the form for change detection, sorting each item's startTimeConditions by (trigger, candidate) so the
   * comparison is order-insensitive: editing a mapping rebuilds that array as [...others, ...added], which reorders
   * rows without changing content and would otherwise trigger a false "unsaved changes" prompt.
   */
  private serializeForm(): string {
    const value = this.bundleForm.getRawValue();
    (value.days || []).forEach((day: any) => (day.items || []).forEach((item: any) => {
      if (Array.isArray(item.startTimeConditions)) {
        item.startTimeConditions = [...item.startTimeConditions].sort((a, b) =>
          (a.triggerStartTime || '').localeCompare(b.triggerStartTime || '') ||
          (a.candidateStartTime || '').localeCompare(b.candidateStartTime || ''));
      }
    }));
    return JSON.stringify(value);
  }

  /**
   * Within each day, every tour must start after the occupied window of all earlier tours on that day.
   * The occupied window ends at a tour's latest selected start time plus its duration (when the activity
   * has been loaded), otherwise at its latest start time. The constraint resets at the start of each day.
   */
  private sequentialStartTimesValidator: ValidatorFn = (): ValidationErrors | null => {
    // Barrier overlaps are surfaced as non-blocking warnings (the manager decides); only a structural issue
    // (a later conditional item with no start-time mapping at all) blocks the form.
    return this.computeStartTimeViolations().some(v => v.blocking) ? { startTimeBlocking: true } : null;
  };

  /**
   * All start-time issues across days. Barrier OVERLAPS are WARNINGS (blocking:false): any later tour (Bokun or
   * Liva) that would start before the preceding tours' occupied window - each preceding tour's duration +
   * travel-to-next + check-in-before-the-next-tour. Conditional mode also adds a BLOCKING "must map at least one
   * start time" check.
   */
  private computeStartTimeViolations(): StartTimeViolation[] {
    const days = this.days;
    if (!days || !days.controls) {
      return [];
    }

    const conditional = !!this.bundleForm?.get('conditionalStartTimes')?.value;
    const violations: StartTimeViolation[] = [];

    days.controls.forEach((dayControl: AbstractControl) => {
      const dayNo = dayControl.get('dayNo')?.value;
      const items = dayControl.get('items') as UntypedFormArray;
      if (!items || !items.controls.length) {
        return;
      }

      // Only ACTIVE items participate: WidgetApi is fed active items only and picks the day's first item from them,
      // so the trigger and the barrier must be resolved the same way here (matches BundleController).
      const activeItems = items.controls
        .map(c => c.value)
        .filter(v => v.active === true)
        .sort((a, b) => (a.position || 0) - (b.position || 0));

      if (activeItems.length < 2) {
        return;
      }

      const firstItem = activeItems[0];
      const titleOf = (item: any) => item.customTitle || item.titleInInvSys;
      const push = (item: any, blocking: boolean, message: string) =>
        violations.push({ dayNo, position: item.position, title: titleOf(item), blocking, message });

      if (conditional) {
        // Trigger/candidate mapping is keyed on the inventory SLOTS (StartTimeInInvSys), matching WidgetApi — the
        // customized time is display-only there. (effectiveStartMinutes, with the customized time, is used only for
        // the barrier/overlap maths below, where the customized time is the real-world start.)
        const firstStartMinutes = this.slotStartMinutes(firstItem);
        const firstOccupied = (Number(firstItem.durationMinutes) || 0) + (Number(firstItem.travelTimeMinutes) || 0) + (Number(firstItem.checkInMinutes) || 0);

        activeItems.slice(1).forEach(item => {
          const title = titleOf(item);
          const conditions = (item.startTimeConditions || []) as BundleItemStartTimeCondition[];
          if (!conditions.length) {
            push(item, true, `"${title}" has no start times mapped to the first tour's start times.`);
            return;
          }

          // Mirror the server's structural rules (ValidateConditionalStartTimes) as BLOCKING violations, so a form
          // that would 400 on save is caught here instead of silently passing.
          const itemStartMinutes = this.slotStartMinutes(item);
          const mappedTriggers = new Set<number>();
          conditions.forEach(condition => {
            const trigger = parseTimeToMinutes(condition.triggerStartTime);
            const candidate = parseTimeToMinutes(condition.candidateStartTime);
            if (trigger === null || candidate === null) {
              // Mirror the server: an unparseable mapping time is blocking, not silently skipped.
              push(item, true, `"${title}" has a start-time mapping with an invalid time.`);
              return;
            }
            mappedTriggers.add(trigger);
            if (!itemStartMinutes.includes(candidate)) {
              push(item, true, `"${title}" is mapped to start time ${formatMinutes(candidate)} which is no longer one of its start times.`);
            }
            if (!firstStartMinutes.includes(trigger)) {
              push(item, true, `"${title}" is mapped to first-tour start ${formatMinutes(trigger)} which the first tour no longer offers.`);
            }
            const minStart = trigger + firstOccupied;
            // A barrier at/after midnight means every same-day candidate would be "before" it and the recommended
            // time would wrap into the next day (misleading). Next-day continuations aren't supported, so suppress
            // the advisory here — the bundle-item hint suppresses in lock-step (isCandidateBeforeTrigger).
            if (minStart < MINUTES_PER_DAY && candidate < minStart) {
              push(item, false, `"${title}" starts before ${formatMinutes(minStart)} — it overlaps the first tour plus travel and check-in. You can keep this if the schedule is intentional.`);
            }
          });

          // Every first-tour start time must map at least one candidate; grouped into one message per item.
          const unmapped = firstStartMinutes.filter(fs => !mappedTriggers.has(fs)).sort((a, b) => a - b).map(formatMinutes);
          if (unmapped.length) {
            push(item, true, `"${title}" has no start time mapped for first-tour start(s) ${unmapped.join(', ')}.`);
          }
        });
      } else {
        // Latest minute-of-day occupied by an already-processed tour on this day, null before the first.
        let barrier: number | null = null;
        activeItems.forEach(item => {
          const startMinutes = this.effectiveStartMinutes(item);
          if (!startMinutes.length) {
            return;
          }
          // Every item (Bokun or Liva) is flagged when it overlaps the previous tour — the scheduling fields
          // (duration/travel/check-in) apply to any item, so classic mode matches conditional mode. Overlap =
          // warning only. A barrier at/after midnight is suppressed (next-day continuations aren't supported).
          if (barrier !== null && barrier < MINUTES_PER_DAY && Math.min(...startMinutes) < barrier) {
            push(item, false, `"${titleOf(item)}" starts before ${formatMinutes(barrier)} — it overlaps the previous tour's time plus travel and check-in. You can keep this if the schedule is intentional.`);
          }
          const occupiedUntil = Math.max(...startMinutes) + (Number(item.durationMinutes) || 0) + (Number(item.travelTimeMinutes) || 0) + (Number(item.checkInMinutes) || 0);
          barrier = barrier === null ? occupiedUntil : Math.max(barrier, occupiedUntil);
        });
      }
    });

    return violations;
  }

  /**
   * The effective start times of a bundle item as minutes-of-day. When the item uses a customized start
   * time that overrides the inventory-system slot, only the customized time applies.
   */
  private effectiveStartMinutes(item: any): number[] {
    if (item.customized && item.customizedTime) {
      const customized = parseTimeToMinutes(item.customizedTime);
      return customized === null ? [] : [customized];
    }

    const startingTimes = (item.startingTimes || []) as BundleItemStartTime[];
    return startingTimes
      .map(st => parseTimeToMinutes(st.startTimeInInvSys))
      .filter((minutes): minutes is number => minutes !== null);
  }

  /**
   * Recomputes the cached start-time state (violations + per-day/per-item info) once per form value change. The
   * template binds the cached fields, so it never re-walks the days on a change-detection pass and array identities
   * (e.g. firstItemStartTimes) stay stable across passes.
   */
  private recomputeStartTimeInfo(): void {
    this.startTimeViolations = this.computeStartTimeViolations();
    this.daysStartTimeInfo = this.days.controls.map(dayControl => {
      const controls = (dayControl.get('items') as UntypedFormArray).controls;
      // First ACTIVE item by position — the trigger tour, resolved the same way WidgetApi does.
      const activeSorted = controls
        .filter(c => c.value.active === true)
        .sort((a, b) => (a.value.position || 0) - (b.value.position || 0));
      const firstControl = activeSorted.length ? activeSorted[0] : null;
      const first = firstControl ? firstControl.value : null;

      // Trigger options are the inventory slots (StartTimeInInvSys), NOT the customized display time.
      const firstItemStartTimes = first
        ? Array.from(new Set(((first.startingTimes || []) as BundleItemStartTime[])
          .map(st => st.startTimeInInvSys).filter((t): t is string => !!t))).sort()
        : [];
      const firstItemOccupiedMinutes = first
        ? (Number(first.durationMinutes) || 0) + (Number(first.travelTimeMinutes) || 0) + (Number(first.checkInMinutes) || 0)
        : 0;

      const itemBarriers: (number | null)[] = [];
      const itemIsFirstActive: boolean[] = [];
      controls.forEach(control => {
        itemIsFirstActive.push(control === firstControl);
        if (control.value.active !== true) {
          itemBarriers.push(null);
          return;
        }
        // Barrier = occupied-until of the preceding ACTIVE items by position (same set the day validator uses).
        const rank = activeSorted.indexOf(control);
        let barrier: number | null = null;
        for (let i = 0; i < rank; i++) {
          const item = activeSorted[i].value;
          const startMinutes = this.effectiveStartMinutes(item);
          if (!startMinutes.length) {
            continue;
          }
          const occupiedUntil = Math.max(...startMinutes) + (Number(item.durationMinutes) || 0)
            + (Number(item.travelTimeMinutes) || 0) + (Number(item.checkInMinutes) || 0);
          barrier = barrier === null ? occupiedUntil : Math.max(barrier, occupiedUntil);
        }
        itemBarriers.push(barrier);
      });

      return { firstItemStartTimes, firstItemOccupiedMinutes, itemBarriers, itemIsFirstActive };
    });
  }

  /**
   * Drop each later item's conditional mappings whose trigger start time is no longer one of the first item's start
   * times. The editor renders exactly one trigger select per firstItemStartTimes, so a mapping keyed on a removed
   * trigger has no control to clear it — yet the server rejects it as a blocking violation, dead-ending Save. Run on
   * value changes so deselecting a first-tour start time self-heals (the candidate side already self-heals via
   * onConditionCandidatesChange). emitEvent:false — the caller re-validates/recomputes right after.
   */
  private pruneOrphanedTriggerConditions(): void {
    if (!this.bundleForm?.get('conditionalStartTimes')?.value) {
      return;
    }
    this.days.controls.forEach((dayControl: AbstractControl) => {
      const items = dayControl.get('items') as UntypedFormArray;
      const activeSorted = (items?.controls || [])
        .filter(c => c.value.active === true)
        .sort((a, b) => (a.value.position || 0) - (b.value.position || 0));
      if (activeSorted.length < 2) {
        return;
      }
      const first = activeSorted[0].value;
      // Triggers are the first item's inventory slots, matched by minute-of-day (tolerates "HH:mm" vs "HH:mm:ss").
      const triggerMinutes = new Set(((first.startingTimes || []) as BundleItemStartTime[])
        .map(st => parseTimeToMinutes(st.startTimeInInvSys))
        .filter((m): m is number => m !== null));
      activeSorted.slice(1).forEach(control => {
        const conditionsCtrl = control.get('startTimeConditions') as UntypedFormControl;
        const conditions = (conditionsCtrl?.value || []) as BundleItemStartTimeCondition[];
        if (!conditions.length) {
          return;
        }
        const kept = conditions.filter(c => {
          const trigger = parseTimeToMinutes(c.triggerStartTime);
          return trigger !== null && triggerMinutes.has(trigger);
        });
        if (kept.length !== conditions.length) {
          conditionsCtrl.setValue(kept, { emitEvent: false });
        }
      });
    });
  }

  /** Whether the item is the day's first ACTIVE item by position (the trigger tour) — reads the cached info. */
  isFirstActiveItem(dayIndex: number, itemIndex: number): boolean {
    return this.daysStartTimeInfo[dayIndex]?.itemIsFirstActive[itemIndex] ?? false;
  }

  /** The first item's inventory-system start times (the trigger options for later items) — cached. */
  getFirstItemStartTimes(dayIndex: number): string[] {
    return this.daysStartTimeInfo[dayIndex]?.firstItemStartTimes ?? [];
  }

  /** A bundle item's inventory-system start times (StartTimeInInvSys) as unique minutes-of-day. */
  private slotStartMinutes(item: any): number[] {
    const minutes = ((item.startingTimes || []) as BundleItemStartTime[])
      .map(st => parseTimeToMinutes(st.startTimeInInvSys))
      .filter((m): m is number => m !== null);
    return Array.from(new Set(minutes));
  }

  /** The first item's occupied window length (duration + travel to next + check-in) on a day, in minutes — cached. */
  getFirstItemOccupiedMinutes(dayIndex: number): number {
    return this.daysStartTimeInfo[dayIndex]?.firstItemOccupiedMinutes ?? 0;
  }

  /** The cached start-time violations for a given day, for display in the template (recomputed on valueChanges). */
  getDayStartTimeViolations(dayNo: number): StartTimeViolation[] {
    return this.startTimeViolations.filter(v => v.dayNo === dayNo);
  }

  ngOnDestroy(): void {
    this.formValueChanges.unsubscribe();
    this.conditionalToggleChanges?.unsubscribe();
  }

  onDefaultPricingCategoryChange(categoryIndex: number): void {
    this.pricingCategories.controls.forEach((fg: UntypedFormGroup, i: number) => {
      if (i !== categoryIndex)
        fg.get('defaultCategory').setValue(false);
    });
  }

  addPricingCategory(): void {
    const highestBundleCategoryId = Math.max(...this.pricingCategories.value.map((i: PricingCategory) => i.id));
    const newPricingCategory = this.fb.group({
      id: [highestBundleCategoryId + 1],
      title: ['', Validators.required],
      maxAge: [null],
      minAge: [null],
      defaultCategory: [false]
    });

    this.pricingCategories.push(newPricingCategory);

    this.days.controls.forEach((d: UntypedFormGroup) => {
      const itemsFormArray = d.get('items') as UntypedFormArray;

      itemsFormArray.controls.forEach((i: UntypedFormGroup) => {
        const pricingCategories = i.get('pricingCategories') as UntypedFormArray;
        const newBundleItemPricingCategory = this.fb.group({
          bundlePricingCategoryId: [highestBundleCategoryId + 1],
          pricingCategoryIdInInvSys: [null, Validators.required],
          pricingCategoryTitleInInvSys: [null],
        });

        pricingCategories.push(newBundleItemPricingCategory);
      });
    });

    this.bundleItems.toArray().forEach(i => i.onLoadItemData());
  }

  removePricingCategory(index: number, id: number): void {
    this.pricingCategories.removeAt(index);

    this.days.controls.forEach((d: UntypedFormGroup) => {
      const items = d.get('items') as UntypedFormArray;

      items.controls.forEach((i: UntypedFormGroup) => {
        const pricingCategories = i.get('pricingCategories') as UntypedFormArray;
        const itemCategoryIndex = pricingCategories.value.findIndex((c: BundleItemPricingCategory) => c.bundlePricingCategoryId === id);

        pricingCategories.removeAt(itemCategoryIndex);
      });
    });
  }

  addDay(dayIndex: number): void {
    const question = `Do you want to add a new day before or after Day ${dayIndex + 1}`;
    const data = new BooleanModalData(question, 'Before', 'After');

    const dialogRef = this.dialog.open(BooleanModalComponent, {
      width: '300px',
      data
    });

    dialogRef.afterClosed().subscribe((isAfter: boolean) => {
      if (isAfter) {
        const newDayIndex = dayIndex + 1;
        const newDay = this.fb.group({
          dayNo: [newDayIndex],
          items: this.fb.array([])
        });

        this.days.insert(newDayIndex, newDay);
        this.setDayNoPropsAccordingToIndex();
      }
      else if (isAfter === false) {
        const newDay = this.fb.group({
          dayNo: [dayIndex],
          items: this.fb.array([])
        });

        this.days.insert(dayIndex, newDay);
        this.setDayNoPropsAccordingToIndex();
      }
    });
  }

  addLastDay(): void {
    const newDay = this.fb.group({
      dayNo: [this.days.controls.length + 1],
      items: this.fb.array([])
    });

    this.days.push(newDay);
  }

  removeDay(index: number): void {
    const question = 'Are you sure you want to remove this day? Items under this day will be removed as well.';
    const data = new BooleanModalData(question);

    const dialogRef = this.dialog.open(BooleanModalComponent, {
      width: '300px',
      data
    });

    dialogRef.afterClosed().subscribe((res: boolean) => {
      if (res) {
        this.days.removeAt(index);

        this.setDayNoPropsAccordingToIndex();
      }
    });
  }

  addItem(day: UntypedFormGroup): void {
    const items = day.get('items') as UntypedFormArray;

    const dialogRef = this.dialog.open(ItemModalComponent, {
      width: '300px',
      data: {
        showTypeSelect: true
      } as ItemModalData
    });

    dialogRef.afterClosed().subscribe((res: Activity) => {
      if (res) {
        const pricingCategories: BundleItemPricingCategory[] = this.formValue.pricingCategories.map(i => ({
          bundlePricingCategoryId: i.id,
          pricingCategoryIdInInvSys: null,
          pricingCategoryTitleInInvSys: null,
          occupancy: null
        } as BundleItemPricingCategory));
        const pricingCategoryFormGroups = pricingCategories.map(i => this.fb.group(i));
        const pricingCategoriesFormArray = this.fb.array(pricingCategoryFormGroups);

        const position = Math.max(...items.value.map((i: BundleItem) => i.position), 0) + 1;
        const item = new BundleItem(position, day.value.dayNo, res.title, res.id, pricingCategories);
        item.invSys = res.invSys; // preserve the inventory system chosen in the item modal (Bokun/Liva)
        item.durationMinutes = res.durationMinutes ?? null; // prefill duration from the inventory system when available
        const itemFormGroup = this.fb.group(item);

        itemFormGroup.setControl('pricingCategories', pricingCategoriesFormArray);
        itemFormGroup.get('extras').setValue([]);
        itemFormGroup.get('startingTimes').setValue([]);
        itemFormGroup.get('startTimeConditions').setValue([]);
        itemFormGroup.markAllAsTouched();

        items.push(itemFormGroup);
      }
    });
  }

  removeItem(day: UntypedFormGroup, itemIndex: number): void {
    const question = 'Are you sure you want to remove this bundle item?';
    const data = new BooleanModalData(question);

    const dialogRef = this.dialog.open(BooleanModalComponent, {
      width: '300px',
      data
    });

    dialogRef.afterClosed().subscribe((res: boolean) => {
      if (res) {
        const items = day.get('items') as UntypedFormArray;
        items.removeAt(itemIndex);

        this.setPositionPropAccordingToIndex(items);
      }
    });
  }

  onSubmitClick(): void {
    const conditional = !!this.conditionalStartTimes.value;
    this.days.controls.forEach((day: UntypedFormGroup) => {
      const items = day.get('items') as UntypedFormArray;
      const hasLiva = items.controls.some(c => c.value.invSys === InventorySystem.Liva);
      // For conditional or Liva-containing bundles the item ORDER is the schedule (the first item is the trigger),
      // so keep the manual order — re-sorting by start time would let an earlier-starting later tour jump ahead of
      // the first tour and bypass the sequence rule. Classic all-Bokun bundles keep the previous behaviour of
      // ordering by start time on save, so opening one to make an unrelated edit doesn't reorder its tours.
      if (!conditional && !hasLiva) {
        this.sortDayItemsByStartTime(items);
      }
      this.setPositionPropAccordingToIndex(items);
    });

    this.formSubmitted.emit();
  }

  /**
   * Reorders a day's item controls (active first, then earliest effective start time; items without a start time go
   * last). Active-first matches the previous behaviour so a deactivated item sinks to the bottom and an all-Bokun
   * bundle round-trips its active items untouched.
   */
  private sortDayItemsByStartTime(items: UntypedFormArray): void {
    const earliest = (item: any): number => {
      const minutes = this.effectiveStartMinutes(item);
      return minutes.length ? Math.min(...minutes) : Number.MAX_SAFE_INTEGER;
    };
    const sorted = [...items.controls].sort((a, b) => {
      const activeDiff = (b.value.active === true ? 1 : 0) - (a.value.active === true ? 1 : 0);
      return activeDiff !== 0 ? activeDiff : earliest(a.value) - earliest(b.value);
    });
    items.clear({ emitEvent: false });
    sorted.forEach(control => items.push(control, { emitEvent: false }));
  }

  /**
   * The occupied-until minute-of-day of the preceding ACTIVE tours on the item's day — used to flag earlier
   * start-time options (any item, Bokun or Liva) with a non-blocking warning. Reads the cached per-item value
   * (computed by recomputeStartTimeInfo).
   */
  getPrecedingBarrierMinutes(dayIndex: number, itemIndex: number): number | null {
    const info = this.daysStartTimeInfo[dayIndex];
    return info ? (info.itemBarriers[itemIndex] ?? null) : null;
  }

  private setDayNoPropsAccordingToIndex(): void {
    this.days.controls.forEach((d: UntypedFormGroup, index) => {
      const newDayNo = index + 1;
      const dayDayNo = d.get('dayNo') as UntypedFormControl;
      dayDayNo.setValue(newDayNo);

      const items = d.get('items') as UntypedFormArray;

      items.controls.forEach((i: UntypedFormGroup) => {
        const itemDayNo = i.get('dayNo');
        itemDayNo.setValue(newDayNo);
      });
    });
  }

  private setPositionPropAccordingToIndex(items: UntypedFormArray): void {
    items.controls.forEach((item, index) => {
      const position = item.get('position');

      position.setValue(index + 1);
    });
  }

  private loadBundleForm(bundle: Bundle): void {
    this.bundleForm = this.fb.group({
      title: ['', Validators.required],
      description: [''],
      discount: [''],
      conditionalStartTimes: [false],
      category: [],
      pricingCategories: this.fb.array([
        this.fb.group({
          defaultCategory: [false],
          id: [0],
          maxAge: [null],
          minAge: [null],
          title: ['', Validators.required]
        })
      ]),
      pickupRequired: [false],
      dropoffRequired: [false],
      days: this.fb.array([
        this.fb.group({
          dayNo: [1],
          items: this.fb.array([])
        })
      ])
    });

    if (!this.isNew) {
      const { pricingCategories, days } = this.bundle;
      const categoryFormGroups = pricingCategories.map(i => this.fb.group(i));
      const categoriesFormArray = this.fb.array(categoryFormGroups);
      this.pricingCategories = categoriesFormArray;

      const dayFormGroups = days.map(i => this.fb.group(i));
      const daysFormArray = this.fb.array(dayFormGroups);

      daysFormArray.controls.forEach((i: UntypedFormGroup, dayIndex: number) => {
        const itemFormGroups = days[dayIndex].items.map((item: BundleItem) => this.fb.group(item));

        itemFormGroups.forEach((g: UntypedFormGroup, itemIndex) => {
          const pricingCategoryFormGroups = days[dayIndex].items[itemIndex].pricingCategories.map(category => this.fb.group(category));
          const pricingCategoriesFormArray = this.fb.array(pricingCategoryFormGroups);

          g.setControl('pricingCategories', pricingCategoriesFormArray);
        });

        const itemsFormArray = this.fb.array(itemFormGroups);
        i.setControl('items', itemsFormArray);
      });

      this.days = daysFormArray;
      this.bundleForm.patchValue(bundle);
    }
  }
}
